import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { Save, Phone, MessageCircle, MapPin, Home } from "lucide-react";

// House locations with their full addresses
const HOUSE_LOCATIONS = [
  { id: "dada_estate", name: "Halelluyah Area, Dada Estate, Osogbo" },
  { id: "ile_tuntun", name: "Aina Close, Ile-TunTun, Ibadan" },
  { id: "adetunji", name: "Adetunji Area, Osogbo" },
  { id: "aimasiko", name: "Aimasiko Area, Osogbo" },
  { id: "alakia", name: "Alakia Area, Ibadan" },
];

// Apartments/Units for each location
const APARTMENTS_BY_LOCATION = {
  "dada_estate": [
    "One-Bedroom unit 1",
    "One-Bedroom unit 2",
    "One-Bedroom unit 3",
    "Two-Bedroom unit"
  ],
  "ile_tuntun": [
    "One-Bedroom unit 1",
    "One-Bedroom unit 2",
    "Three-Bedroom unit"
  ],
  "adetunji": [
    "Unit 1",
    "Unit 2",
    "Unit 3",
    "Unit 4"
  ],
  "aimasiko": [
    "Unit 1",
    "Unit 2",
    "Unit 3",
    "Unit 4",
    "Unit 5"
  ],
  "alakia": [
    "Unit 1",
    "Unit 2",
    "Unit 3",
    "Unit 4",
    "Unit 5",
    "Unit 6",
    "Unit 7",
    "Unit 8",
    "Unit 9",
    "Unit 10"
  ]
};

const PAYMENT_FREQUENCIES = [
  "Monthly",
  "Quarterly",
  "Every six months",
  "Yearly"
];

export default function TenantInfoUpdatePage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [formData, setFormData] = useState({
    last_name: "",
    first_name: "",
    mobile_phone: "",
    whatsapp_phone: "",
    house_location: "",
    apartment_number: "",
    payment_frequency: "",
    duration_living: "",
    last_payment_amount: "",
    last_payment_date: "",
    months_years_in_arrears: "",
    amount_in_arrears: "",
    next_payment_date: ""
  });

  // Get available apartments based on selected location
  const availableApartments = selectedLocationId ? APARTMENTS_BY_LOCATION[selectedLocationId] || [] : [];

  // Find location ID from location name (for loading existing data)
  const findLocationIdByName = (name) => {
    const location = HOUSE_LOCATIONS.find(loc => loc.name === name);
    return location ? location.id : "";
  };

  // Handle location change - reset apartment when location changes
  const handleLocationChange = (locationId) => {
    setSelectedLocationId(locationId);
    const location = HOUSE_LOCATIONS.find(loc => loc.id === locationId);
    setFormData(prev => ({
      ...prev,
      house_location: location ? location.name : "",
      apartment_number: "" // Reset apartment when location changes
    }));
  };

  useEffect(() => {
    fetchTenantInfo();
  }, []);

  const fetchTenantInfo = async () => {
    try {
      const response = await axios.get(`${API}/tenant-info/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setFormData({
          last_name: response.data.last_name || "",
          first_name: response.data.first_name || "",
          mobile_phone: response.data.mobile_phone || "",
          whatsapp_phone: response.data.whatsapp_phone || "",
          house_location: response.data.house_location || "",
          apartment_number: response.data.apartment_number || "",
          payment_frequency: response.data.payment_frequency || "",
          duration_living: response.data.duration_living || "",
          last_payment_amount: response.data.last_payment_amount || "",
          last_payment_date: response.data.last_payment_date || "",
          months_years_in_arrears: response.data.months_years_in_arrears || "",
          amount_in_arrears: response.data.amount_in_arrears || "",
          next_payment_date: response.data.next_payment_date || ""
        });
        // Set the location ID if we have existing data
        if (response.data.house_location) {
          const locationId = findLocationIdByName(response.data.house_location);
          setSelectedLocationId(locationId);
        }
      }
    } catch (error) {
      // No existing info, that's fine
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.last_name || !formData.first_name || !formData.house_location || 
        !formData.apartment_number || !formData.payment_frequency || 
        !formData.duration_living || !formData.last_payment_amount ||
        !formData.last_payment_date || !formData.next_payment_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    // Contact info validation - at least one required
    if (!formData.mobile_phone && !formData.whatsapp_phone) {
      toast.error("Please provide at least one contact number (Mobile or WhatsApp)");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/tenant-info`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Tenant information updated successfully!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update information");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="tenant-info-update-page">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 pt-20 md:pt-6 md:p-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-primary">Tenant Information Update</h1>
            <p className="text-text-muted mt-1">Update your tenant details and payment information</p>
          </div>

          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-xl">Your Information</CardTitle>
              <CardDescription>
                Please fill out all the fields below
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Details */}
                <fieldset className="border border-stone-200 rounded-xl p-4">
                  <legend className="px-2 text-sm font-medium text-primary">Personal Details</legend>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last Name *</Label>
                      <Input
                        id="last_name"
                        data-testid="tenant-lastname-input"
                        placeholder="Enter your last name"
                        value={formData.last_name}
                        onChange={(e) => handleChange("last_name", e.target.value)}
                        className="h-12 bg-stone-50 border-stone-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First Name *</Label>
                      <Input
                        id="first_name"
                        data-testid="tenant-firstname-input"
                        placeholder="Enter your first name"
                        value={formData.first_name}
                        onChange={(e) => handleChange("first_name", e.target.value)}
                        className="h-12 bg-stone-50 border-stone-200"
                      />
                    </div>
                  </div>
                </fieldset>

                {/* Contact Information */}
                <fieldset className="border border-stone-200 rounded-xl p-4">
                  <legend className="px-2 text-sm font-medium text-primary flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Update Contact Information (at least one required)
                  </legend>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="mobile_phone" className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-stone-500" />
                        Mobile Tel #
                      </Label>
                      <Input
                        id="mobile_phone"
                        data-testid="tenant-mobile-phone-input"
                        placeholder="e.g., +234 801 234 5678"
                        value={formData.mobile_phone}
                        onChange={(e) => handleChange("mobile_phone", e.target.value)}
                        className="h-12 bg-stone-50 border-stone-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="whatsapp_phone" className="flex items-center gap-2">
                        <MessageCircle className="w-4 h-4 text-green-600" />
                        WhatsApp # (preferred)
                      </Label>
                      <Input
                        id="whatsapp_phone"
                        data-testid="tenant-whatsapp-phone-input"
                        placeholder="e.g., +234 801 234 5678"
                        value={formData.whatsapp_phone}
                        onChange={(e) => handleChange("whatsapp_phone", e.target.value)}
                        className="h-12 bg-stone-50 border-stone-200"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-stone-500 mt-2">
                    WhatsApp is preferred for faster communication
                  </p>
                </fieldset>

                {/* Location Details */}
                <fieldset className="border border-stone-200 rounded-xl p-4">
                  <legend className="px-2 text-sm font-medium text-primary flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Location Details
                  </legend>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-stone-500" />
                        House Location *
                      </Label>
                      <Select 
                        value={selectedLocationId} 
                        onValueChange={handleLocationChange}
                      >
                        <SelectTrigger className="h-12 bg-stone-50 border-stone-200" data-testid="tenant-location-select">
                          <SelectValue placeholder="Select your house location" />
                        </SelectTrigger>
                        <SelectContent>
                          {HOUSE_LOCATIONS.map((location) => (
                            <SelectItem key={location.id} value={location.id}>
                              {location.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Home className="w-4 h-4 text-stone-500" />
                        Apartment/Unit Occupied *
                      </Label>
                      <Select 
                        value={formData.apartment_number} 
                        onValueChange={(value) => handleChange("apartment_number", value)}
                        disabled={!selectedLocationId}
                      >
                        <SelectTrigger className="h-12 bg-stone-50 border-stone-200" data-testid="tenant-apartment-select">
                          <SelectValue placeholder={selectedLocationId ? "Select your unit" : "Select location first"} />
                        </SelectTrigger>
                        <SelectContent>
                          {availableApartments.map((apt) => (
                            <SelectItem key={apt} value={apt}>{apt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!selectedLocationId && (
                        <p className="text-xs text-amber-600">
                          Please select a house location first
                        </p>
                      )}
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="duration_living">How long have you been living in this apartment/Shop? *</Label>
                      <Input
                        id="duration_living"
                        data-testid="tenant-duration-input"
                        placeholder="e.g., 2 years 3 months"
                        value={formData.duration_living}
                        onChange={(e) => handleChange("duration_living", e.target.value)}
                        className="h-12 bg-stone-50 border-stone-200"
                      />
                    </div>
                  </div>
                </fieldset>

                {/* Payment Details */}
                <fieldset className="border border-stone-200 rounded-xl p-4">
                  <legend className="px-2 text-sm font-medium text-primary">Payment Details</legend>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Payment Frequency *</Label>
                      <Select 
                        value={formData.payment_frequency} 
                        onValueChange={(value) => handleChange("payment_frequency", value)}
                      >
                        <SelectTrigger className="h-12 bg-stone-50 border-stone-200" data-testid="tenant-frequency-select">
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                        <SelectContent>
                          {PAYMENT_FREQUENCIES.map((freq) => (
                            <SelectItem key={freq} value={freq}>{freq}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_payment_amount">Last Payment Amount (₦) *</Label>
                      <Input
                        id="last_payment_amount"
                        data-testid="tenant-amount-input"
                        placeholder="e.g., 150,000"
                        value={formData.last_payment_amount}
                        onChange={(e) => handleChange("last_payment_amount", e.target.value)}
                        className="h-12 bg-stone-50 border-stone-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_payment_date">Date of Last Payment *</Label>
                      <Input
                        id="last_payment_date"
                        data-testid="tenant-last-payment-input"
                        type="date"
                        value={formData.last_payment_date}
                        onChange={(e) => handleChange("last_payment_date", e.target.value)}
                        className="h-12 bg-stone-50 border-stone-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="next_payment_date">Date of Next Payment *</Label>
                      <Input
                        id="next_payment_date"
                        data-testid="tenant-next-payment-input"
                        type="date"
                        value={formData.next_payment_date}
                        onChange={(e) => handleChange("next_payment_date", e.target.value)}
                        className="h-12 bg-stone-50 border-stone-200"
                      />
                    </div>
                  </div>
                </fieldset>

                {/* Arrears Details */}
                <fieldset className="border border-stone-200 rounded-xl p-4">
                  <legend className="px-2 text-sm font-medium text-primary">Arrears Information (if applicable)</legend>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="months_years_in_arrears">Month/Years in Arrears</Label>
                      <Input
                        id="months_years_in_arrears"
                        data-testid="tenant-arrears-duration-input"
                        placeholder="e.g., 2 months"
                        value={formData.months_years_in_arrears}
                        onChange={(e) => handleChange("months_years_in_arrears", e.target.value)}
                        className="h-12 bg-stone-50 border-stone-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="amount_in_arrears">Amount in Arrears (₦)</Label>
                      <Input
                        id="amount_in_arrears"
                        data-testid="tenant-arrears-amount-input"
                        placeholder="e.g., 50,000"
                        value={formData.amount_in_arrears}
                        onChange={(e) => handleChange("amount_in_arrears", e.target.value)}
                        className="h-12 bg-stone-50 border-stone-200"
                      />
                    </div>
                  </div>
                </fieldset>

                <div className="flex flex-col sm:flex-row justify-end gap-4">
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/dashboard")}
                    className="rounded-full order-2 sm:order-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    className="rounded-full bg-primary hover:bg-primary-800 order-1 sm:order-2"
                    disabled={loading}
                    data-testid="submit-tenant-info-btn"
                  >
                    {loading ? "Saving..." : "Submit Information"}
                    <Save className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
